import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useTheme } from '@hooks';
import { defaultTooltip } from '@components/Charts/ChartJsTooltipHelpers';
import { hoverLinePlugin, syncTooltipHiderPlugin, yAxisTitle } from '@components/ChartJsPlugins';
import { Box, Stack } from '@esgian/esgianui';
import moment from 'moment/moment';
import Chartjs from '@components/Charts/Chartjs';
import { getStackedBarChartMax, parseNumberToSignificant } from '@helpers';
import { barHoverPlugin } from '@components/ChartJsPlugins/BarHoverEffect';
import { highlightPeriodPlugin } from '@components/ChartJsPlugins/HighlightPeriodPlugin';
import CanvasLegends from '@components/Charts/CanvasTimeSeriesChart/CanvasLegends';

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  BarElement,
  BarController,
  CategoryScale,
  TimeScale,
  LinearScale,
  Filler,
  Tooltip,
  zoomPlugin
);

const getOptions = (
  theme,
  data,
  syncRefs,
  max,
  min,
  unit,
  stackedBar,
  type,
  highlightPeriod,
  includeTooltipSum,
  hideEmptyTooltipValues,
  hideCurrentTooltip,
  secondaryYAxis,
  customTooltipHandler,
  handleLogClick,
  handlePointClick,
  calculateYMax,
  fixTooltipPosition,
  displayZeroTooltipValues,
  tooltipDatasetMaxHeightPx,
  positionTooltipAboveChart,
  customScrollbarStyle
) => {
  const {
    mode,
    palette: {
      opacity: { five: opacityFive, ten: opacityTen },
      text: { primary },
      neutral: { neutral04 }
    }
  } = theme;
  let period = {};
  if (highlightPeriod) {
    period = {
      startIndex: highlightPeriod.startIndex,
      endIndex: highlightPeriod.endIndex
    };
  }
  let axisMax = max;
  if (axisMax > 1000) {
    axisMax = axisMax * 1.1;
  }
  if (axisMax >= 3) {
    if (calculateYMax) {
      axisMax = Math.ceil(axisMax / 5) * 5;
    }
  } else {
    axisMax = axisMax * 1.1;
  }

  let options = {
    commonUpdate: true,
    onClick: function (event) {
      if (!handlePointClick) return;
      const { chart } = event;

      const activePoint = chart.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: false },
        true
      );
      if (activePoint.length) {
        handlePointClick(activePoint[0]);
      }
    },

    onHover: function (event) {
      if (!handlePointClick) return;
      const chartCanvas = event.native.target;
      chartCanvas.style.cursor = 'pointer';
    },
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30
      }
    },
    scales: {
      x: {
        min: 0,
        offset: true,
        type: 'category',
        labels: data?.labels ?? [],
        ticks: {
          callback: function (index) {
            let currentDate = moment.utc(this.getLabelForValue(index));
            if (index >= data?.labels.length) {
              currentDate.format('MMM DD');
            }
            let nextDate = moment.utc(this.getLabelForValue(index + 1));
            let rollup = 'hour';
            if (nextDate.diff(currentDate, 'hours') < 1) {
              rollup = 'min';
            }

            if (currentDate.clone().hour() === 0 && rollup === 'hour') {
              return currentDate.format('MMM DD');
            }
            if (currentDate.clone().startOf('day').isSame(currentDate) && rollup === 'min') {
              return currentDate.format('MMM DD');
            }
            return currentDate.format('HH:mm');
          },
          color: primary,
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10,
            family: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(',')
          }
        },
        grid: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false
        }
      },
      y: {
        stacked: stackedBar,
        border: {
          display: false
        },
        grid: {
          tickLength: 0,
          color: `${neutral04}7F`
        },
        afterFit: function (scaleInstance) {
          let stepSize = `${Math.round((axisMax * 1.5) / 5) * 4}`.length;
          let size = stepSize < 4 ? 4 : stepSize;
          scaleInstance.width = size * 8; // sets the width to 100px
        },
        ticks: {
          color: primary,
          font: {
            size: 10,
            family: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(',')
          },
          beginAtZero: true,
          callback: (val) =>
            axisMax >= 5 ? parseInt(val) : calculateYMax ? parseNumberToSignificant(val) : val,
          maxTicksLimit: calculateYMax ? 6 : max + 1, // +1 for zero
          stepSize: Math.round(axisMax / 5),
          max: axisMax,
          count: calculateYMax ? 6 : max + 1,
          precision: 2,
          xMin: 0,
          autoSkip: false
        },
        min: min,
        max: axisMax,
        type: 'linear',
        position: 'left',
        beginAtZero: true
      }
    },
    elements: {
      line: {
        tension: 0
      },
      point: {
        radius: 0
      }
    },
    tooltips: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      tooltip: {
        callbacks: {},
        enabled: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        position: 'nearest',
        external: (context) =>
          customTooltipHandler
            ? customTooltipHandler(context, data)
            : defaultTooltip(
                {
                  context: context,
                  data: data,
                  theme: theme,
                  unit: unit,
                  includeTooltipSum: includeTooltipSum,
                  hideEmptyValues: hideEmptyTooltipValues,
                  hideCurrentTooltip: hideCurrentTooltip,
                  syncRefs: syncRefs,
                  handleLogClick: handleLogClick,
                  fixTooltipPosition: fixTooltipPosition,
                  displayZeroValues: displayZeroTooltipValues,
                  datasetMaxHeightPx: tooltipDatasetMaxHeightPx,
                  positionAboveChart: positionTooltipAboveChart,
                  isHorizontal: false
                })
      },
      yAxisTitle: {
        fontColor: primary,
        fontSize: '12px',
        text: unit
      },
      barHoverBackground: {
        syncRefs: syncRefs,
        backgroundColor: opacityTen
      },
      highlightPeriod: {
        startIndex: period.startIndex ?? null,
        endIndex: period.endIndex ?? null,
        color: opacityFive
      },
      syncTooltipHiderPlugin: {
        syncRefs: syncRefs
      },
      hoverLinePlugin: {
        syncRefs: syncRefs,
        lineColor: primary
      },
      zoom: {
        pan: {
          onPan({ chart }) {
            syncRefs.forEach(({ current }) =>
              current?.zoomScale(
                'x',
                { min: Math.trunc(chart.scales.x.min), max: Math.trunc(chart.scales.x.max) },
                'none'
              )
            );
          },
          enabled: true,
          mode: 'x'
        },
        limits: {
          x: { min: 'original', max: 'original', minRange: 10 }
        },
        zoom: {
          mode: 'x',
          onZoom({ chart }) {
            if (chart.scales.max < 10) {
              chart.scales.x.max = 10;
            }
            syncRefs.forEach(({ current }) =>
              current?.zoomScale(
                'x',
                { min: Math.trunc(chart.scales.x.min), max: Math.trunc(chart.scales.x.max) },
                'none'
              )
            );
          },
          drag: {
            enabled: false
          },
          pinch: {
            enabled: true
          },
          wheel: {
            enabled: true,
            modifierKey: 'ctrl'
          }
        }
      }
    }
  };
  if (secondaryYAxis) {
    options.scales.y1 = secondaryYAxis;
  }
  return options;
};

function CanvasTimeSeriesChart({
  highlightPeriod,
  syncRefs,
  id,
  series,
  loading,
  unit,
  maxY,
  minY,
  chartRef,
  hideCurrentTooltip,
  customTooltipHandler,
  handleLogClick,
  handlePointClick,
  hideEmptyTooltipValues,
  height,
  includeTooltipSum,
  enableBarHover,
  secondaryYAxis,
  type,
  stackedBar,
  disableHoverLinePlugin,
  calculateYMax,
  fixTooltipPosition,
  displayZeroTooltipValues,
  tooltipDatasetMaxHeightPx,
  positionTooltipAboveChart
}) {
  let plugins = [yAxisTitle];
  const { theme, customScrollbar } = useTheme();
  const {
    palette: {
      charts: { sixColorsLine }
    }
  } = theme;

  if (syncRefs.length) {
    plugins.push(syncTooltipHiderPlugin);
  }

  if (!disableHoverLinePlugin) {
    plugins.push(hoverLinePlugin);
  }
  if (enableBarHover) {
    plugins.push(barHoverPlugin);
  }
  if (highlightPeriod) {
    plugins.push(highlightPeriodPlugin);
  }

  const [hiddenLabels, setHiddenLabels] = useState([]);
  const handleToggleLegend = useCallback((label) => {
    setHiddenLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  }, []);

  const data = useMemo(() => {
    if (!series.length) return { labels: [], datasets: [] };
    return {
      labels: series[0].data?.map(({ x }) => x),
      datasets: series.map((val, i) => {
        const label = val.label ?? ''; // Ensure each dataset has a label
        return {
          borderColor: sixColorsLine[i % sixColorsLine.length], // Use modulo to avoid index out of bounds
          borderWidth: 2,
          spanGaps: true,
          pointHitRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          ...val,
          originalBackgroundColor: val.backgroundColor,
          label,
          data: val.data ?? [], // Pass data correctly
          legendHidden: !!val.hidden, // original series-level hidden: exclude helper datasets from legend
          userHidden: hiddenLabels.includes(label), // user deselection: greyed legend item, still toggleable
          hidden: !!val.hidden || hiddenLabels.includes(label) // effective visibility for Chart.js + tooltip
        };
      })
    };
  }, [series, sixColorsLine, hiddenLabels]);

  const options = useMemo(() => {
    if (!data.datasets.length) return null;

    let maxYaxis = maxY;
    if (!maxYaxis) {
      if (stackedBar) {
        maxYaxis = getStackedBarChartMax(series);
      } else {
        maxYaxis = Math.max(...(series.flatMap((s) => s.data.map((d) => d.y)) ?? 1));
      }
    }

    return getOptions(
      theme,
      data,
      syncRefs,
      maxYaxis,
      minY,
      unit,
      stackedBar,
      type,
      highlightPeriod,
      includeTooltipSum,
      hideEmptyTooltipValues,
      hideCurrentTooltip,
      secondaryYAxis,
      customTooltipHandler,
      handleLogClick,
      handlePointClick,
      calculateYMax,
      fixTooltipPosition,
      displayZeroTooltipValues,
      tooltipDatasetMaxHeightPx,
      positionTooltipAboveChart,
      customScrollbar
    );
  }, [data, theme, syncRefs, highlightPeriod]);

  return (
    <Stack sx={{ width: 'auto' }} spacing={1}>
      <CanvasLegends data={data} chartRef={chartRef} id={id} onToggle={handleToggleLegend} />
      <Box sx={{ height: height, width: '100%', position: 'relative' }}>
        <Chartjs
          loading={loading}
          plugins={plugins}
          chartRef={chartRef}
          id={id}
          type={type}
          data={data}
          options={options}
          sx={{ ...customScrollbar }}
        />
      </Box>
    </Stack>
  );
}

CanvasTimeSeriesChart.propTypes = {
  loading: PropTypes.bool,
  stackedBar: PropTypes.bool,
  enableBarHover: PropTypes.bool,
  disableHoverLinePlugin: PropTypes.bool,
  hideEmptyTooltipValues: PropTypes.bool,
  displayZeroTooltipValues: PropTypes.bool,
  includeTooltipSum: PropTypes.bool,
  type: PropTypes.oneOf(['line', 'bar']),
  hideCurrentTooltip: PropTypes.bool,
  height: PropTypes.string,
  maxY: PropTypes.number,
  secondaryYAxis: PropTypes.object,
  minY: PropTypes.number,
  unit: PropTypes.string,
  series: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.string,
  highlightPeriod: PropTypes.shape({
    startIndex: PropTypes.number,
    endIndex: PropTypes.number
  }),
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  syncRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
  ),
  customTooltipHandler: PropTypes.func,
  handlePointClick: PropTypes.func,
  handleLogClick: PropTypes.func,
  calculateYMax: PropTypes.bool,
  fixTooltipPosition: PropTypes.bool,
  tooltipDatasetMaxHeightPx: PropTypes.number,
  positionTooltipAboveChart: PropTypes.bool
};

CanvasTimeSeriesChart.defaultProps = {
  height: '330px',
  unit: '',
  maxY: undefined,
  secondaryYAxis: undefined,
  highlightPeriod: undefined,
  stackedBar: false,
  enableBarHover: false,
  disableHoverLinePlugin: false,
  hideEmptyTooltipValues: false,
  displayZeroTooltipValues: false,
  includeTooltipSum: false,
  type: 'line',
  minY: 0,
  loading: false,
  series: [],
  syncRefs: [],
  id: 'timeSeries',
  chartRef: undefined,
  hideCurrentTooltip: false,
  handleLogClick: undefined,
  handlePointClick: undefined,
  customTooltipHandler: undefined,
  calculateYMax: true,
  fixTooltipPosition: false,
  tooltipDatasetMaxHeightPx: undefined,
  positionTooltipAboveChart: false
};

export default CanvasTimeSeriesChart;
